import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AuthenticationService, RedirectAuthService } from '@alfresco/adf-core';
import { WkAppConfigService } from './wk-app-config.service';
import { WkAlfrescoApiService } from './wk-alfresco-api.service';
import { WkCookieService } from './wk-cookie.service';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { AlfrescoApi } from '@alfresco/js-api';

export const APOLLO_SSO_COOKIE_KEY = 'apollo-sso';

@Injectable({
  providedIn: 'root',
})
export class WkAuthenticationService extends AuthenticationService {
  private readonly serviceInit: boolean;

  private readonly wkAppConfigService: WkAppConfigService;
  private readonly apiService: WkAlfrescoApiService;
  private readonly wkCookieService: WkCookieService;
  private readonly http: HttpClient;
  private alfrescoApi: AlfrescoApi;

  constructor(
    private readonly wkInjector: Injector,
    redirectAuthService: RedirectAuthService,
    private readonly alfrescoApiService: AlfrescoApiService
  ) {
    super(wkInjector, redirectAuthService);
    this.wkAppConfigService =
      this.wkInjector.get<WkAppConfigService>(WkAppConfigService);
    this.apiService =
      wkInjector.get<WkAlfrescoApiService>(WkAlfrescoApiService);
    this.wkCookieService = wkInjector.get<WkCookieService>(WkCookieService);
    this.http = wkInjector.get<HttpClient>(HttpClient);

    this.alfrescoApiService.alfrescoApiInitialized.subscribe((ready) => {
      if (ready) {
        this.alfrescoApi = new AlfrescoApi(
          this.alfrescoApiService.currentAppConfig
        );
      }
    });
    if (!this.serviceInit && this.isSsoCookieSet()) {
      this.init();
    }
    this.serviceInit = true;
  }

  private init() {
    this.apiService.alfrescoApiInitialized.subscribe((ready) => {
      if (ready) {
        const api = this.apiService.getInstance();

        api.on('error', (error: { status: number }) => {
          if (error.status === 401) {
            this.removeSsoCookie();
            this.invalidateSsoSession();
          }
        });

        this.isGatewaySessionExists().subscribe((session) => {
          if (!session) {
            this.removeSsoCookie();
          }
        });
      }
    });
  }

  public isLoggedIn(): boolean {
    return this.isSsoLoggedIn() || super.isLoggedIn();
  }

  public isEcmLoggedIn(): boolean {
    return this.isSsoLoggedIn() || super.isEcmLoggedIn();
  }

  public isBpmLoggedIn(): boolean {
    return this.isSsoLoggedIn() || super.isBpmLoggedIn();
  }

  public isSsoLoggedIn(): boolean {
    const isSsoUser = this.isSsoEnabled() && this.isSsoCookieSet();
    if (isSsoUser) {
      localStorage.setItem('isSsoUser', 'true');
      console.log(
        '✅ SSO user detected - cookie found:',
        APOLLO_SSO_COOKIE_KEY
      );
    } else {
      localStorage.removeItem('isSsoUser');
      console.log('❌ SSO login failed:', {
        ssoEnabled: this.isSsoEnabled(),
        cookieSet: this.isSsoCookieSet(),
        cookieValue: this.wkCookieService.getItem(APOLLO_SSO_COOKIE_KEY),
      });
    }
    return isSsoUser;
  }

  public isSsoEnabled(): boolean {
    return JSON.parse(this.wkAppConfigService.get('wkSso', 'false'));
  }

  private isSsoCookieSet(): boolean {
    // Check both cookie (production) and localStorage (local mock testing)
    const cookie = this.wkCookieService.getItem(APOLLO_SSO_COOKIE_KEY);
    const localStorage = window.localStorage.getItem(APOLLO_SSO_COOKIE_KEY);

    const exists = cookie !== null || localStorage !== null;
    if (exists) {
      console.log('🍪 SSO session found:', {
        cookie: cookie !== null,
        localStorage: localStorage !== null,
      });
    }
    return exists;
  }

  private setSsoCookie() {
    this.wkCookieService.setItem(APOLLO_SSO_COOKIE_KEY, '1', null, null);
  }

  private removeSsoCookie() {
    this.wkCookieService.deleteCookie(APOLLO_SSO_COOKIE_KEY);
  }

  public logout(): Observable<any> {
    if (this.isSsoCookieSet()) {
      this.destroyGatewaySession().subscribe();
      this.removeSsoCookie();
      window.localStorage.removeItem(APOLLO_SSO_COOKIE_KEY);
      localStorage.removeItem('isSsoUser');
      this.invalidateSsoSession();
      localStorage.removeItem('ssoLoggedIn');
      this.onLogout.next(true);
      return of({});
    }
    return super.logout();
  }

  public initSsoSession() {
    this.setSsoCookie();
  }

  public invalidateSsoSession() {
    this.alfrescoApi.invalidateSession();
  }

  private isGatewaySessionExists(): Observable<boolean> {
    return this.http.get('/.sso/sessions').pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  private destroyGatewaySession(): Observable<boolean> {
    return this.http.get('/.sso/terminate').pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  public getTicketEcm(): string {
    return this.apiService.getInstance().getTicketEcm();
  }

  public isPublicUrl(): boolean {
    return this.apiService.getInstance().isPublicUrl();
  }

  public ssoImplicitLogin() {
    return this.apiService.getInstance().implicitLogin();
  }

  getContentClientBasicAuthToken() {
    if (this.isSsoLoggedIn()) {
      return null;
    }
    const { username, password } =
      this.apiService.getInstance().contentClient.authentications.basicAuth;
    return btoa(`${username}:${password}`);
  }
}
